/**
 * Support assistant client (D10-UI, WP3 #3604).
 *
 * Talks to the WP1 backend contract (see
 * docs/requirements/D10-Beyond-Support-Assistant-Requirements.md, "Chat stream
 * contract"):
 *
 *  - `GET  /api/v1/support/status/` -- open; `{enabled, chat_requires_auth,
 *    assistant_name?, mode?, has_used_chat?}`.
 *  - `GET  /api/v1/support/kb/`     -- signed-in; the read-only help corpus for
 *    offline help search. Fetched on demand, never bundled into the SPA.
 *  - `POST /api/v1/support/chat/`   -- signed-in; a Server-Sent Events stream
 *    (`meta` → `token`… (a `replace` may reset the text: words that claimed an
 *    action the server did not run are withdrawn) → `action`? → `error`? →
 *    `done`), or one `offline`
 *    frame / `{mode:"offline"}` JSON when no provider can serve the turn.
 *
 * `apiRequest` returns parsed JSON and cannot stream, and `EventSource` cannot
 * POST a body or send a bearer header, so the chat call is a raw `fetch` read
 * through `response.body.getReader()` and parsed by hand.
 *
 * Auth failure path (decided here, per the issue): the access token is checked
 * before the server sends its first byte, so an expired token surfaces as a
 * 401/403 *response*, never mid-stream. That case is refreshed once and retried,
 * exactly like `apiRequest`. A connection that drops after the stream has begun
 * is NOT silently reconnected -- replaying the turn would bill and log it twice
 * -- so the message ends in a local error state and the user can ask again.
 */
import {
  ANCHOR_ID_RE,
  isCatalogAnchor,
  isRecordCatalogAnchor,
} from "../lib/support/anchorCatalog";
import {
  apiRequest,
  getAccessToken,
  notifySessionExpired,
  refreshSession,
  responseIsAuthExpiry,
} from "./client";

const CHAT_URL = "/api/v1/support/chat/";

/** Server-side cap on one message (D10-API-007); longer is a 400. */
export const MAX_MESSAGE_CHARS = 2000;

// --- status + knowledge pack -------------------------------------------------

export type SupportMode = "live" | "mock" | "offline";

export interface SupportStatus {
  enabled: boolean;
  chat_requires_auth?: boolean;
  /** Display name from the backend's SUPPORT_ASSISTANT_NAME (never hard-coded). */
  assistant_name?: string;
  /** Present for a signed-in user while the service is enabled. */
  mode?: SupportMode;
  /**
   * Signed-in only, while enabled: the caller has asked the assistant something
   * before, on any device (D10-UI-008). A yes/no about the caller themselves.
   */
  has_used_chat?: boolean;
}

export function getSupportStatus(signal?: AbortSignal): Promise<SupportStatus> {
  return apiRequest<SupportStatus>("/support/status/", {}, signal);
}

export interface KbSection {
  id: string;
  kind: string;
  title: string;
  text: string;
}

export interface KbTour {
  id: string;
  title: string;
  start_route?: string;
}

export interface KbPage {
  route: string;
  label: string;
  group: string;
  purpose: string;
}

export interface SupportKb {
  version: number;
  sections: KbSection[];
  tours: KbTour[];
  pages: KbPage[];
}

export function getSupportKb(signal?: AbortSignal): Promise<SupportKb> {
  return apiRequest<SupportKb>("/support/kb/", {}, signal);
}

/**
 * The conversation's transcript excerpt for the `open_feedback` pre-fill
 * (D10-ACT-002). Built on the server from the stored, scrubbed messages and
 * already fenced and labelled as untrusted user-supplied content (D10-SEC-006);
 * the browser never assembles it from its own, unscrubbed, message list.
 * Owner-only: another user's conversation id is a 404.
 */
// --- admin insights (D10-OPS-001, D10-SEC-009, #3607) ---------------------------

export type SupportInsightsPeriod = 7 | 30 | 90;

export interface SupportInsightsVolumeDay {
  date: string;
  conversations: number;
  questions: number;
}

export interface SupportInsightsUnansweredRow {
  id: number;
  /** Scrubbed question text (personal details already masked server-side). */
  question: string;
  route: string;
  /** The asker's role/preset label -- never a name or e-mail. */
  role: string;
  created_at: string;
  /** The agency-local calendar day (YYYY-MM-DD), the same day the chart uses. */
  date?: string;
  reason: "offline" | "no_answer" | "not_helpful";
}

export interface SupportInsights {
  enabled: boolean;
  pilot_mode: boolean;
  /** `timezone` is the deployment's (agency's) zone every date is computed in. */
  period: { days: number; start: string; end: string; choices: number[]; timezone?: string };
  totals: {
    conversations: number;
    questions: number;
    replies: number;
    unanswered: number;
    offline_replies: number;
    actions: number;
    rejected_actions: number;
    /** Refused actions that were the model's own mistakes (never counted as misuse). */
    model_invalid_actions?: number;
    suspected_injection: number;
    spend_usd: string;
  };
  volume: SupportInsightsVolumeDay[];
  /** `title` is the most specific help heading; `source` the guide it is from. */
  topics: { id: string; title: string; source: string; count: number }[];
  actions: { name: string; count: number }[];
  injection_flags: { flag: string; count: number }[];
  unanswered: {
    count: number;
    page: number;
    page_size: number;
    num_pages: number;
    results: SupportInsightsUnansweredRow[];
  };
}

/** Admin-only support insights (`GET /api/v1/support/insights/`). */
export function getSupportInsights(
  params: { period: SupportInsightsPeriod; page: number },
  signal?: AbortSignal,
): Promise<SupportInsights> {
  const qs = new URLSearchParams({ period: String(params.period), page: String(params.page) });
  return apiRequest<SupportInsights>(`/support/insights/?${qs.toString()}`, {}, signal);
}

export async function getSupportExcerpt(
  conversationId: number,
  signal?: AbortSignal,
): Promise<string> {
  const data = await apiRequest<{ excerpt?: unknown }>(
    `/support/conversations/${conversationId}/excerpt/`,
    {},
    signal,
  );
  return typeof data.excerpt === "string" ? data.excerpt : "";
}

// --- actions (D10-ACT, re-validated client-side per D10-SEC-002) ---------------

/**
 * Action names the browser knows how to carry out. The server validates first;
 * the client validates again before anything runs, and drops everything else.
 * WP4 (#3605) carries out `start_tour`/`open_feedback`; WP5 (#3606) adds
 * `navigate`/`highlight`.
 */
export const SUPPORT_ACTION_NAMES = [
  "start_tour",
  "open_feedback",
  "navigate",
  "highlight",
] as const;
export type SupportActionName = (typeof SUPPORT_ACTION_NAMES)[number];

/** Mirrors the backend FEEDBACK_TYPES / Feedback.FeedbackType. */
const FEEDBACK_TYPES = new Set(["bug", "data_integrity", "suggestion", "feature"]);
const MAX_SUMMARY_CHARS = 300;
const MAX_ID_CHARS = 64;
/** Mirrors the backend MAX_HINT_CHARS: a highlight tooltip's text. */
export const MAX_HINT_CHARS = 200;
/** Markup, links and control/invisible characters never reach a tooltip. */
const UNSAFE_HINT_RE =
  // eslint-disable-next-line no-control-regex
  /(:\/\/|\bwww\.|javascript:|data:|vbscript:|<|>|\]\(|[\u0000-\u001f\u007f\u200b-\u200f\u2028-\u202e\u2060-\u2064\ufeff])/i;

export type SupportAction =
  | { name: "start_tour"; args: { tour_id: string } }
  | {
      name: "open_feedback";
      args: { type: "bug" | "data_integrity" | "suggestion" | "feature"; summary: string };
    }
  | { name: "navigate"; args: { route: string } }
  | { name: "highlight"; args: { anchor: string; route: string; text: string } };

export interface ActionAllowedLists {
  allowedTours: readonly string[];
  /** The pages the user may open (the nav command registry), exact routes. */
  allowedRoutes?: readonly string[];
  /** The page the message was sent from; a record-page highlight must stay on it. */
  currentRoute?: string;
}

/**
 * May `highlight` point at `anchor` on `route`? Either a static page the user
 * may open with the anchor catalogued there, or -- for a control on a record
 * page (#3633) -- exactly the record page the user is on (`currentRoute`), with
 * the anchor documented for that kind of record. A highlight never carries the
 * user to a record page: it has no id to open, and a route that is not the
 * current page is refused.
 */
export function isAllowedHighlight(
  anchor: string,
  route: unknown,
  allowedRoutes: readonly string[],
  currentRoute: string | undefined,
): boolean {
  if (typeof route !== "string") return false;
  if (isAllowedSupportRoute(route, allowedRoutes)) return isCatalogAnchor(anchor, route);
  return (
    typeof currentRoute === "string" &&
    route === currentRoute &&
    isRecordCatalogAnchor(anchor, route, allowedRoutes)
  );
}

/**
 * Is `route` one the user may be sent to? Exact membership of the allowed list
 * only -- no prefixes, record (`:param`) routes, queries or fragments -- so the
 * assistant can only ever move the browser within the app's own known pages.
 */
export function isAllowedSupportRoute(route: unknown, allowedRoutes: readonly string[]): boolean {
  return (
    typeof route === "string" &&
    route.startsWith("/") &&
    !route.startsWith("//") &&
    !/[?#:\\]/.test(route) &&
    allowedRoutes.includes(route)
  );
}

function dropAction(reason: string, raw: unknown): null {
  if (import.meta.env.DEV) {
    console.warn(`support: dropped streamed action (${reason})`, raw);
  }
  return null;
}

/**
 * Re-validate a streamed `action` against a closed schema and the user's own
 * allowed lists. Returns a typed action, or `null` (logged in dev) for anything
 * unknown, malformed, over-length or outside the lists.
 */
export function validateSupportAction(
  raw: unknown,
  lists: ActionAllowedLists,
): SupportAction | null {
  if (!raw || typeof raw !== "object") return dropAction("not an object", raw);
  const { name, args } = raw as { name?: unknown; args?: unknown };
  if (typeof name !== "string" || !(SUPPORT_ACTION_NAMES as readonly string[]).includes(name)) {
    return dropAction("unknown action", raw);
  }
  if (!args || typeof args !== "object" || Array.isArray(args)) {
    return dropAction("bad args", raw);
  }
  const a = args as Record<string, unknown>;
  const keys = Object.keys(a);
  if (name === "start_tour") {
    const tourId = a.tour_id;
    if (keys.length !== 1 || typeof tourId !== "string" || tourId.length > MAX_ID_CHARS) {
      return dropAction("bad start_tour args", raw);
    }
    if (!lists.allowedTours.includes(tourId)) return dropAction("tour not allowed", raw);
    return { name, args: { tour_id: tourId } };
  }
  const allowedRoutes = lists.allowedRoutes ?? [];
  if (name === "navigate") {
    const route = a.route;
    if (keys.length !== 1 || typeof route !== "string") {
      return dropAction("bad navigate args", raw);
    }
    if (!isAllowedSupportRoute(route, allowedRoutes)) return dropAction("route not allowed", raw);
    return { name, args: { route } };
  }
  if (name === "highlight") {
    const { anchor, route, text } = a;
    if (
      keys.some((k) => k !== "anchor" && k !== "route" && k !== "text") ||
      typeof anchor !== "string" ||
      !ANCHOR_ID_RE.test(anchor) ||
      typeof text !== "string" ||
      !text.trim() ||
      text.length > MAX_HINT_CHARS ||
      UNSAFE_HINT_RE.test(text)
    ) {
      return dropAction("bad highlight args", raw);
    }
    if (!isAllowedHighlight(anchor, route, allowedRoutes, lists.currentRoute)) {
      return dropAction("anchor not catalogued on an allowed page", raw);
    }
    return { name, args: { anchor, route: route as string, text: text.trim() } };
  }
  // open_feedback
  const type = a.type;
  const summary = a.summary;
  if (
    keys.some((k) => k !== "type" && k !== "summary") ||
    typeof type !== "string" ||
    !FEEDBACK_TYPES.has(type) ||
    typeof summary !== "string" ||
    summary.length > MAX_SUMMARY_CHARS ||
    /(:\/\/|javascript:|data:|<|>|\]\()/i.test(summary)
  ) {
    return dropAction("bad open_feedback args", raw);
  }
  return {
    name: "open_feedback",
    args: { type: type as "bug" | "data_integrity" | "suggestion" | "feature", summary },
  };
}

// --- Server-Sent Events parsing ------------------------------------------------

export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Incremental `text/event-stream` parser. Feed it decoded text chunks as they
 * arrive; it returns every complete frame and keeps the partial tail buffered.
 * Handles `\n`, `\r\n` and `\r` line endings, multi-line `data:`, comments and
 * frames split across chunk boundaries.
 */
export class SseParser {
  private buffer = "";

  push(chunk: string): SseFrame[] {
    this.buffer += chunk.replace(/\r\n?/g, "\n");
    const frames: SseFrame[] = [];
    let idx = this.buffer.indexOf("\n\n");
    while (idx !== -1) {
      const block = this.buffer.slice(0, idx);
      this.buffer = this.buffer.slice(idx + 2);
      const frame = parseBlock(block);
      if (frame) frames.push(frame);
      idx = this.buffer.indexOf("\n\n");
    }
    return frames;
  }

  /** Flush a final frame the server closed without a trailing blank line. */
  end(): SseFrame[] {
    const rest = this.buffer;
    this.buffer = "";
    const frame = rest.trim() ? parseBlock(rest) : null;
    return frame ? [frame] : [];
  }
}

function parseBlock(block: string): SseFrame | null {
  let event = "message";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  if (data.length === 0) return null;
  return { event, data: data.join("\n") };
}

// --- chat stream -------------------------------------------------------------

export interface ChatRequestBody {
  conversation_id?: number | null;
  message: string;
  route: string;
  allowed_routes: string[];
  allowed_tours: string[];
}

export type OfflineReason = "budget" | "busy" | "unavailable" | "disabled";

export type SupportStreamEvent =
  | { type: "meta"; conversationId: number | null; assistantName?: string; mode?: SupportMode }
  | { type: "token"; text: string }
  /** The server withdrew the words shown so far: the reply's text becomes `text`. */
  | { type: "replace"; text: string }
  | { type: "action"; raw: unknown }
  | { type: "error"; message: string }
  | { type: "done"; truncated: boolean }
  | { type: "offline"; reason: OfflineReason; conversationId: number | null };

export class SupportChatError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "SupportChatError";
  }
}

const FRIENDLY_ERRORS: Record<number, string> = {
  400: "That message could not be sent. Please shorten it and try again.",
  429: "You're sending messages quickly. Please wait a moment and try again.",
};
const GENERIC_ERROR = "Sorry, something went wrong. Please try again.";

function safeJson(text: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(text);
    return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function toEvent(frame: SseFrame): SupportStreamEvent | null {
  const data = safeJson(frame.data) ?? {};
  switch (frame.event) {
    case "meta":
      return {
        type: "meta",
        conversationId: typeof data.conversation_id === "number" ? data.conversation_id : null,
        assistantName: typeof data.assistant_name === "string" ? data.assistant_name : undefined,
        mode: typeof data.mode === "string" ? (data.mode as SupportMode) : undefined,
      };
    case "token":
      return typeof data.text === "string" ? { type: "token", text: data.text } : null;
    case "replace":
      return typeof data.text === "string" ? { type: "replace", text: data.text } : null;
    case "action":
      return { type: "action", raw: data };
    case "error":
      return {
        type: "error",
        message: typeof data.message === "string" ? data.message : GENERIC_ERROR,
      };
    case "done":
      return { type: "done", truncated: Boolean(data.truncated) };
    case "offline":
      return offlineEvent(data);
    default:
      return null;
  }
}

function offlineEvent(data: Record<string, unknown>): SupportStreamEvent {
  const reason = data.reason;
  return {
    type: "offline",
    reason:
      reason === "budget" || reason === "busy" || reason === "unavailable"
        ? reason
        : "unavailable",
    conversationId: typeof data.conversation_id === "number" ? data.conversation_id : null,
  };
}

async function postChat(body: ChatRequestBody, signal?: AbortSignal): Promise<Response> {
  const send = (token: string | null) =>
    fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

  const response = await send(getAccessToken());
  if (!(await responseIsAuthExpiry(response))) return response;

  // Refresh once and retry, like apiRequest. The stream has not started, so
  // nothing is replayed.
  const refreshed = await refreshSession();
  if (refreshed) {
    const retry = await send(refreshed);
    if (!(await responseIsAuthExpiry(retry))) return retry;
  }
  notifySessionExpired();
  throw new SupportChatError(401, "Your session has expired. Please sign in again.");
}

/**
 * Send one chat turn and yield its events as they stream in. Ends after `done`,
 * `offline`, or the server closing the stream. Throws `SupportChatError` for a
 * request the server refused (auth, validation, throttling) or a connection that
 * failed; aborting via `signal` ends the generator quietly.
 */
export async function* streamSupportChat(
  body: ChatRequestBody,
  signal?: AbortSignal,
): AsyncGenerator<SupportStreamEvent> {
  let response: Response;
  try {
    response = await postChat(body, signal);
  } catch (err) {
    if (err instanceof SupportChatError) throw err;
    if (signal?.aborted) return;
    throw new SupportChatError(0, "Couldn't reach the help service. Please try again.");
  }

  // Kill switch flipped mid-session: the endpoint 404s. Fall back to offline help.
  if (response.status === 404) {
    yield { type: "offline", reason: "disabled", conversationId: null };
    return;
  }
  if (!response.ok) {
    throw new SupportChatError(response.status, FRIENDLY_ERRORS[response.status] ?? GENERIC_ERROR);
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("text/event-stream") || !response.body) {
    const data = safeJson(await response.text()) ?? {};
    if (data.mode === "offline") yield offlineEvent(data);
    else throw new SupportChatError(response.status, GENERIC_ERROR);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch {
        if (signal?.aborted) return;
        throw new SupportChatError(0, "The connection dropped. Please ask again.");
      }
      const frames = chunk.done
        ? [...parser.push(decoder.decode()), ...parser.end()]
        : parser.push(decoder.decode(chunk.value, { stream: true }));
      for (const frame of frames) {
        const event = toEvent(frame);
        if (!event) continue;
        yield event;
        if (event.type === "done" || event.type === "offline") return;
      }
      if (chunk.done) return;
    }
  } finally {
    reader.releaseLock();
  }
}
